function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from 'react';
import { QueryReportHeaderReact } from 'common/query-builder/query-report-header';
import { Entity, EntityType } from 'common/report/util/saved-entities/types';
import { SearchDisplayContext } from 'common/report/util/search/types';
import { HeaderAction, QueryReportHeaderParams } from 'common/query-builder/query-report-header/types';
import { buildQueryReportHeaderParams } from 'common/report/util/saved-entities';
import { HeatMap } from 'common/widgets/heat-maps/types';
import { Dashboard } from 'common/report/dashboards/types';
import { getRouteToCurrentDashboard } from 'common/report/util/boards';
import { navigate } from 'common/util/url';
import { getReportUrl } from 'common/report/util';
import { DashboardOrigin } from 'common/report/util/bookmark-overrides';
import styles from './index.module.styl';
type HeatMapPageHeaderProps = {
  projectId: number;
  workspaceId: number;
  userId: number;
  heatMap: HeatMap;
  saving: boolean;
  canEdit: boolean;
  heatMapEntity: Entity;
  heatMapDashboard?: Dashboard;
  onEditHeatMap: (heatMap: HeatMap) => void;
  onDuplicateHeatMap: (heatMap: HeatMap) => void;
  onDeleteHeatMap: (heatMap: HeatMap) => void;
  onSaveNewHeatMap: (heatMap: HeatMap, dashboardId: number, createNew: boolean, shouldDuplicate: boolean) => void;
};
export function HeatMapPageHeader(props: HeatMapPageHeaderProps) {
  const queryReportHeaderParams: QueryReportHeaderParams = React.useMemo(() => {
    return _objectSpread(_objectSpread({}, buildQueryReportHeaderParams({
      projectId: props.projectId,
      entityType: EntityType.HeatMap,
      entity: props.heatMapEntity!,
      displayContext: SearchDisplayContext.HeatMap
    })), {}, {
      displayContext: SearchDisplayContext.HeatMap,
      entityType: EntityType.HeatMap,
      trackName: "[Heat Maps]",
      overflowControlItems: [],
      hasUnsavedChanges: false,
      invalidMessage: "",
      projectId: props.projectId,
      workspaceId: props.workspaceId,
      userId: props.userId,
      selectedBookmarkDashboard: props.heatMapDashboard as DashboardOrigin,
      dashboardsRoute: getRouteToCurrentDashboard({
        dashboard: props.heatMapDashboard as DashboardOrigin,
        includeCurrentDashboard: true
      }) || [],
      isSavingBookmark: props.saving,
      isTemplateDraftDashboard: !props.heatMapEntity.id
    });
  }, [props.heatMapEntity, props.heatMap, props.heatMapDashboard, props.saving]);
  const handleHeaderActionClick = React.useCallback((ev: CustomEvent) => {
    const actionName = ev.detail.name || ev.detail.value;
    switch (actionName) {
      case HeaderAction.Delete:
        props.onDeleteHeatMap(props.heatMap);
        break;
      case HeaderAction.Duplicate:
        props.onDuplicateHeatMap(props.heatMap);
        break;
    }
  }, [props.onDeleteHeatMap, props.onDuplicateHeatMap, props.heatMap]);
  const handleSave = React.useCallback((ev: CustomEvent<{
    name: string;
    description: string;
    dashboard?: {
      id: number;
    };
    saveAsNew: boolean;
  }>) => {
    var _ev$detail$dashboard;
    const newName = ev.detail.name;
    const newDescription = ev.detail.description;
    const saveAsNew = ev.detail.saveAsNew;
    const dashboardId = (_ev$detail$dashboard = ev.detail.dashboard) === null || _ev$detail$dashboard === void 0 ? void 0 : _ev$detail$dashboard.id;
    if (saveAsNew && props.heatMap.dashboard_id) {
      // This is the case where we are duplicating the heat map on the same dashboard
      props.onSaveNewHeatMap(props.heatMap, props.heatMap.dashboard_id, false, true);
    } else if (saveAsNew && dashboardId) {
      if (props.canEdit) {
        if (props.heatMap.id) {
          // This is the case where we are putting the heat map on a new dashboard
          props.onSaveNewHeatMap(props.heatMap, dashboardId, false, false);
        } else {
          // This is the case where we are saving a new heat map on a new dashboard
          props.onSaveNewHeatMap(props.heatMap, dashboardId, true, false);
        }
      } else {
        // This is the case where we are saving a new heat map on a new dashboard
        props.onSaveNewHeatMap(props.heatMap, dashboardId, true, true);
      }
    } else {
      props.onEditHeatMap(_objectSpread(_objectSpread({}, props.heatMap), {}, {
        title: newName,
        description: newDescription,
        dashboard_id: dashboardId || props.heatMap.dashboard_id
      }));
    }
  }, [props.heatMap, props.onEditHeatMap]);
  const handleBackToBoard = React.useCallback(() => {
    if (props.heatMapDashboard) {
      navigate("".concat(getReportUrl("boards", {
        projectId: props.projectId,
        workspaceId: props.workspaceId
      }), "#id=").concat(props.heatMapDashboard.id));
    }
  }, [props.heatMap, props.heatMapDashboard]);
  return <div className={styles.container} data-sentry-component="HeatMapPageHeader" data-sentry-source-file="index.tsx">
      <QueryReportHeaderReact {...queryReportHeaderParams} onActionclick={handleHeaderActionClick} onSave={handleSave} onBackToBoard={handleBackToBoard} data-sentry-element="QueryReportHeaderReact" data-sentry-source-file="index.tsx" />
    </div>;
}